import React from 'react';
import styled from 'styled-components';
import { FiUpload, FiTrash2, FiPlus, FiMove, FiChevronUp, FiChevronDown } from 'react-icons/fi';

const DynamicSectionsForm = ({ dynamicSections = [], onChange, onImageChange }) => {
  
  // Add new section
  const addSection = () => {
    const newSection = {
      enabled: true,
      layout: 'card-left',
      title: '',
      paragraph1: '',
      paragraph2: '',
      features: ['', '', '', ''],
      imageUrl: '',
      imageAlt: '',
      imagePreview: '',
      order: dynamicSections.length
    };
    onChange('dynamicSections', [...dynamicSections, newSection]);
  };

  // Remove section
  const removeSection = (index) => {
    if (!window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
      return;
    }
    const updatedSections = dynamicSections.filter((_, i) => i !== index);
    // Re-order remaining sections
    const reorderedSections = updatedSections.map((section, i) => ({
      ...section,
      order: i
    }));
    onChange('dynamicSections', reorderedSections);
  };

  // Update section field
  const updateSection = (index, field, value) => {
    const updatedSections = [...dynamicSections];
    updatedSections[index] = {
      ...updatedSections[index],
      [field]: value
    };
    onChange('dynamicSections', updatedSections);
  };

  // Update section feature
  const updateFeature = (sectionIndex, featureIndex, value) => {
    const updatedSections = [...dynamicSections];
    const updatedFeatures = [...(updatedSections[sectionIndex].features || ['', '', '', ''])];
    updatedFeatures[featureIndex] = value;
    updatedSections[sectionIndex] = {
      ...updatedSections[sectionIndex],
      features: updatedFeatures
    };
    onChange('dynamicSections', updatedSections);
  };

  // Move section up/down
  const moveSection = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === dynamicSections.length - 1)
    ) {
      return;
    }

    const updatedSections = [...dynamicSections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap sections
    [updatedSections[index], updatedSections[targetIndex]] = 
    [updatedSections[targetIndex], updatedSections[index]];
    
    // Update order numbers
    updatedSections[index].order = index;
    updatedSections[targetIndex].order = targetIndex;
    
    onChange('dynamicSections', updatedSections);
  };

  // Handle image upload
  const handleImageUpload = (sectionIndex, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should not exceed 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const updatedSections = [...dynamicSections];
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        imagePreview: reader.result
      };
      onChange('dynamicSections', updatedSections);
    };
    reader.readAsDataURL(file);

    // Call parent handler with file
    onImageChange(`dynamicSectionImage_${sectionIndex}`, file);
  };

  // Remove image
  const removeImage = (sectionIndex) => {
    const updatedSections = [...dynamicSections];
    updatedSections[sectionIndex] = {
      ...updatedSections[sectionIndex],
      imageUrl: '',
      imagePreview: ''
    };
    onChange('dynamicSections', updatedSections);
    onImageChange(`dynamicSectionImage_${sectionIndex}`, null);
  };

  return (
    <FormContainer>
      <FormHeader>
        <h3>Dynamic Sections</h3>
        <p>Add custom sections with flexible layouts (card-left or card-right)</p>
      </FormHeader>

      {dynamicSections.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📋</EmptyIcon>
          <EmptyText>No dynamic sections yet</EmptyText>
          <EmptySubtext>Click "Add Section" to create your first custom section</EmptySubtext>
        </EmptyState>
      ) : (
        <SectionsList>
          {dynamicSections.map((section, index) => (
            <SectionCard key={index}>
              <SectionHeader $disabled={section.enabled === false}>
                <SectionTitle>
                  <FiMove />
                  <span>Section {index + 1}</span>
                  {section.enabled === false && <DisabledBadge>🚫 Disabled</DisabledBadge>}
                  <LayoutBadge layout={section.layout}>
                    {section.layout === 'card-left' ? '📄 ← 🖼️' : '🖼️ → 📄'}
                  </LayoutBadge>
                </SectionTitle>
                <SectionActions>
                  <MoveButton
                    onClick={() => moveSection(index, 'up')}
                    disabled={index === 0}
                    title="Move up"
                  >
                    <FiChevronUp />
                  </MoveButton>
                  <MoveButton
                    onClick={() => moveSection(index, 'down')}
                    disabled={index === dynamicSections.length - 1}
                    title="Move down"
                  >
                    <FiChevronDown />
                  </MoveButton>
                  <ToggleContainer>
                    <ToggleLabel>Enabled</ToggleLabel>
                    <ToggleSwitch
                      type="checkbox"
                      checked={section.enabled !== false}
                      onChange={(e) => updateSection(index, 'enabled', e.target.checked)}
                    />
                  </ToggleContainer>
                  <DeleteButton onClick={() => removeSection(index)}>
                    <FiTrash2 />
                  </DeleteButton>
                </SectionActions>
              </SectionHeader>

              <SectionContent>
                {/* Layout Selection */}
                <FormGroup>
                  <Label>Layout Type</Label>
                  <LayoutSelector>
                    <LayoutOption
                      active={section.layout === 'card-left'}
                      onClick={() => updateSection(index, 'layout', 'card-left')}
                    >
                      <LayoutPreview>📄 🖼️</LayoutPreview>
                      <LayoutName>Card Left</LayoutName>
                      <LayoutDesc>Card on left, image on right</LayoutDesc>
                    </LayoutOption>
                    <LayoutOption
                      active={section.layout === 'card-right'}
                      onClick={() => updateSection(index, 'layout', 'card-right')}
                    >
                      <LayoutPreview>🖼️ 📄</LayoutPreview>
                      <LayoutName>Card Right</LayoutName>
                      <LayoutDesc>Image on left, card on right</LayoutDesc>
                    </LayoutOption>
                  </LayoutSelector>
                </FormGroup>

                {/* Title */}
                <FormGroup>
                  <Label>Section Title *</Label>
                  <Input
                    type="text"
                    value={section.title || ''}
                    onChange={(e) => updateSection(index, 'title', e.target.value)}
                    placeholder="e.g., Your Vision, Our Expertise"
                  />
                </FormGroup>

                {/* Paragraph 1 */}
                <FormGroup>
                  <Label>First Paragraph *</Label>
                  <Textarea
                    value={section.paragraph1 || ''}
                    onChange={(e) => updateSection(index, 'paragraph1', e.target.value)}
                    placeholder="Enter the first paragraph..."
                    rows={3}
                  />
                </FormGroup>

                {/* Paragraph 2 */}
                <FormGroup>
                  <Label>Second Paragraph</Label>
                  <Textarea
                    value={section.paragraph2 || ''}
                    onChange={(e) => updateSection(index, 'paragraph2', e.target.value)}
                    placeholder="Enter the second paragraph (optional)..."
                    rows={3}
                  />
                </FormGroup>

                {/* Features */}
                <FormGroup>
                  <Label>Features (up to 4)</Label>
                  <FeaturesList>
                    {[0, 1, 2, 3].map((featureIndex) => (
                      <FeatureInput
                        key={featureIndex}
                        type="text"
                        value={(section.features && section.features[featureIndex]) || ''}
                        onChange={(e) => updateFeature(index, featureIndex, e.target.value)}
                        placeholder={`Feature ${featureIndex + 1}${featureIndex < 2 ? ' (recommended)' : ' (optional)'}`}
                      />
                    ))}
                  </FeaturesList>
                </FormGroup>

                {/* Image Upload */}
                <FormGroup>
                  <Label>Section Image *</Label>
                  {section.imagePreview || section.imageUrl ? (
                    <ImagePreviewContainer>
                      <ImagePreview src={section.imagePreview || section.imageUrl} alt="Section preview" />
                      <ImageOverlay>
                        <ImageActions>
                          <ImageButton onClick={() => removeImage(index)}>
                            <FiTrash2 /> Remove
                          </ImageButton>
                          <ImageButton as="label">
                            <FiUpload /> Change
                            <HiddenInput
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(index, e)}
                            />
                          </ImageButton>
                        </ImageActions>
                      </ImageOverlay>
                    </ImagePreviewContainer>
                  ) : (
                    <UploadButton as="label">
                      <FiUpload size={24} />
                      <span>Upload Image</span>
                      <small>JPG, PNG, WebP (Max 5MB)</small>
                      <HiddenInput
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(index, e)}
                      />
                    </UploadButton>
                  )}
                </FormGroup>

                {/* Image Alt Text */}
                <FormGroup>
                  <Label>Image Alt Text</Label>
                  <Input
                    type="text"
                    value={section.imageAlt || ''}
                    onChange={(e) => updateSection(index, 'imageAlt', e.target.value)}
                    placeholder="Describe the image (for accessibility)"
                  />
                </FormGroup>
              </SectionContent>
            </SectionCard>
          ))}
        </SectionsList>
      )}

      <AddButton onClick={addSection}>
        <FiPlus /> Add Section
      </AddButton>

      {dynamicSections.length > 0 && (
        <HelpText>
          💡 Tip: Use the arrow buttons to reorder sections. Sections are displayed in the order shown here.
        </HelpText>
      )}
    </FormContainer>
  );
};

export default DynamicSectionsForm;

// Styled Components
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormHeader = styled.div`
  h3 {
    margin: 0 0 8px 0;
    font-size: 20px;
    font-weight: 600;
    color: #1a1a1a;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: #666;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #ddd;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const EmptySubtext = styled.div`
  font-size: 14px;
  color: #666;
`;

const SectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: ${props => props.$disabled ? '#f5f5f5' : '#f8f9fa'};
  border-bottom: 1px solid #e0e0e0;
  opacity: ${props => props.$disabled ? 0.6 : 1};
`;

const DisabledBadge = styled.span`
  font-size: 13px;
  padding: 4px 10px;
  background: #ffebee;
  color: #c62828;
  border-radius: 12px;
  font-weight: 600;
  margin-left: 8px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #333;

  svg {
    color: #666;
  }
`;

const LayoutBadge = styled.span`
  font-size: 14px;
  padding: 4px 12px;
  background: ${props => props.layout === 'card-left' ? '#e3f2fd' : '#f3e5f5'};
  color: ${props => props.layout === 'card-left' ? '#1976d2' : '#7b1fa2'};
  border-radius: 12px;
  font-weight: 500;
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #999;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    color: #666;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
`;

const ToggleLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const ToggleSwitch = styled.input`
  width: 40px;
  height: 20px;
  cursor: pointer;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fdd;
    border-color: #f99;
  }

  svg {
    color: #c33;
  }
`;

const SectionContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }
`;

const LayoutSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const LayoutOption = styled.div`
  padding: 16px;
  border: 2px solid ${props => props.active ? '#4285f4' : '#ddd'};
  background: ${props => props.active ? '#f0f7ff' : 'white'};
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #4285f4;
    background: #f0f7ff;
  }
`;

const LayoutPreview = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const LayoutName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const LayoutDesc = styled.div`
  font-size: 12px;
  color: #666;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ddd;
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ImagePreviewContainer}:hover & {
    opacity: 1;
  }
`;

const ImageActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ImageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
  }

  svg {
    font-size: 16px;
  }
`;

const UploadButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px;
  border: 2px dashed #ddd;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #4285f4;
    background: #f0f7ff;
  }

  span {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  small {
    font-size: 12px;
    color: #666;
  }

  svg {
    color: #4285f4;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #3367d6;
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
  }

  svg {
    font-size: 18px;
  }
`;

const HelpText = styled.div`
  padding: 12px 16px;
  background: #fff3e0;
  border-left: 4px solid #ff9800;
  border-radius: 4px;
  font-size: 13px;
  color: #663c00;
`;
